import {Box, BoxProps, Typography} from "@mui/material"
import {styled} from "@mui/material/styles"
import Grid from "@mui/material/Grid"
import {useTranslation} from "react-i18next"
import {ReactNode} from "react"
import {useSearchParams} from "react-router-dom"

import {PaymentInFullOptionLink} from "./PaymentInFullOptionLink"
import {PartialPaymentOptionLink} from "./PartialPaymentOptionLink"
import {PaymentPlanOptionLink} from "./PaymentPlanOptionLink"
import {PaymentArrangementOptionLink} from "./PaymentArrangementOptionLink"
import {PaymentArrangementInFullOptionLink} from "./PaymentArrangementInFullOptionLink"

import {CustomerFindPayload} from "@indebted/api/customer/customerFind"
import {colors} from "@indebted/theme/colors"

function PaymentOptionSection({PaymentOptions}: {PaymentOptions: CustomerFindPayload["PaymentOptions"]}) {
	const {t} = useTranslation()
	const [searchParams] = useSearchParams()
	const usePaymentArrangementInFull = searchParams.get("usePaymentArrangementInFull") === "true"
	const nonSettlementOptions = PaymentOptions.flatMap((option) => {
		let optionComponent: ReactNode | undefined
		switch (option.Type) {
			case "PaymentInFull": {
				if (usePaymentArrangementInFull) {
					optionComponent = <PaymentArrangementInFullOptionLink />
				} else {
					optionComponent = <PaymentInFullOptionLink {...option.Properties} />
				}
				break
			}
			case "PartialPayment": {
				optionComponent = <PartialPaymentOptionLink />
				break
			}
			case "PaymentPlan": {
				optionComponent = <PaymentPlanOptionLink {...option.Properties} />
				break
			}
			case "PaymentArrangement": {
				optionComponent = <PaymentArrangementOptionLink />
				break
			}
			case "PaymentArrangementInFull": {
				optionComponent = <PaymentArrangementInFullOptionLink />
				break
			}
		}

		if (!optionComponent) {
			return null
		}

		return (
			<Grid
				size={{xs: 12}}
				key={`payment-option-${option.Type}`}
				data-testid={`payment-option-item-${option.Type}`}
				sx={{paddingRight: "0px", paddingLeft: "0px", margin: "0px"}}
			>
				{optionComponent}
			</Grid>
		)
	}).filter((option) => option !== null)

	if (nonSettlementOptions.length === 0) {
		return null
	}

	return (
		<PaymentBox component="section" alignContent="center">
			<Typography
				variant="h5"
				fontWeight={700}
				color="secondary"
				sx={{fontSize: "1.25rem", textAlign: "center", marginBottom: "0px"}}
			>
				{t("customer_get.outstanding_balance.payment_options_label")}
			</Typography>
			<Typography
				variant="subtitle1"
				color={colors.heron["900"]}
				sx={{fontSize: "0.938rem", fontWeight: 500, textAlign: "center", marginBottom: "0px"}}
			>
				{t("customer_get.outstanding_balance.back_on_track_text")}
			</Typography>

			<Grid container spacing={4} sx={{paddingTop: "20px", margin: "0px"}}>
				{nonSettlementOptions}
			</Grid>
		</PaymentBox>
	)
}

const PaymentBox = styled(Box)<BoxProps>`
	width: 100%;
	padding: 1rem 0;

	/* >360px */
	@media (min-width: 360px) {
		padding: 2rem 0;
	}
`

export {PaymentOptionSection}
